import { useEffect, useState } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { PageHero, Eyebrow } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/motion";
import { ARTICLES } from "@/data/content";
import { getBlog } from "@/lib/api";

const Badge = ({ children }) => (
  <span className="inline-block rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold text-gold uppercase tracking-wide">{children}</span>
);

export default function Blog() {
  const [articles, setArticles] = useState(ARTICLES);

  useEffect(() => {
    getBlog()
      .then((data) => { if (Array.isArray(data) && data.length) setArticles(data); })
      .catch(() => {});
  }, []);

  const featured = articles.find((a) => a.featured) || articles[0];
  const rest = articles.filter((a) => a.id !== featured.id);

  return (
    <>
      <PageHero
        eyebrow="Le blog"
        titleLines={["Décryptages des", "marchés africains"]}
        subtitle="Analyses, tendances et conseils opérationnels pour investir et entreprendre en Afrique de l'Ouest."
      />

      <section className="bg-cream py-24 sm:py-32" data-testid="blog-list">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <article className="card-lumen group grid lg:grid-cols-2 gap-0 overflow-hidden rounded-3xl border border-navy/10 bg-white hover:border-gold" data-testid="blog-featured">
              <div className="relative h-72 lg:h-auto overflow-hidden">
                <img src={featured.img} alt={featured.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-10 flex flex-col justify-center">
                <div className="flex items-center gap-4">
                  <Badge>{featured.category}</Badge>
                  <span className="text-xs text-anthracite/60 font-body flex items-center gap-1"><Clock size={13} /> {featured.read}</span>
                </div>
                <h2 className="mt-5 font-display text-3xl text-navy leading-tight">{featured.title}</h2>
                <p className="mt-4 text-anthracite font-body leading-relaxed">{featured.excerpt}</p>
                <p className="mt-4 text-xs text-anthracite/50 font-body">{featured.date}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-semibold text-gold group-hover:gap-3 transition-all">Lire la suite <ArrowRight size={16} /></span>
              </div>
            </article>
          </Reveal>

          <Stagger className="mt-10 grid gap-8 md:grid-cols-3">
            {rest.map((a) => (
              <StaggerItem key={a.id}>
                <article className="card-lumen group flex h-full flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white hover:border-gold" data-testid={`blog-card-${a.id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={a.img} alt={a.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-3">
                      <Badge>{a.category}</Badge>
                      <span className="text-xs text-anthracite/60 font-body flex items-center gap-1"><Clock size={12} /> {a.read}</span>
                    </div>
                    <h3 className="mt-4 font-display text-xl text-navy leading-snug">{a.title}</h3>
                    <p className="mt-2 text-sm text-anthracite font-body flex-1">{a.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-anthracite/50 font-body">{a.date}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold group-hover:gap-2.5 transition-all">Lire <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}
